<script setup lang="ts">
/**
 * 문서 내 [[변수]] 자리.
 *   · combo(nationality/occupation) → 인라인 콤보박스 (값 있으면 selected, 없으면 선택)
 *   · date  → 인라인 편집 + 📅 달력 아이콘 (세션 dateFormat 표시, ISO 저장)
 *   · person/entity → 인라인 편집 + ✨ (챗봇 선택)
 *   · text  → 인라인 직접 편집만
 *   · 드래그앤드롭 지원
 */
import { computed, inject, ref, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDateOnly, normalizeDateInputToIso } from '@/shared/composables/dateUtils';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { CDD_DOC_CTX } from './cddContext';

const props = defineProps<{ fieldKey: string }>();
const ctx = inject(CDD_DOC_CTX)!;
const { t } = useI18n();

const value = computed(() => ctx.valueOf(props.fieldKey));
const label = computed(() => ctx.labelOf(props.fieldKey));
const fieldType = computed(() => ctx.typeOf(props.fieldKey));
const isDate = computed(() => fieldType.value === 'date');
const isAddress = computed(() => fieldType.value === 'address');
const isCombo = computed(() => fieldType.value === 'combo');
const isMsic = computed(() => fieldType.value === 'msic');
const isShareType = computed(() => /^share_type_\d+$/.test(props.fieldKey));
const showIcon = computed(() => ['person', 'entity', 'date', 'address', 'clause', 'msic'].includes(fieldType.value));
const active = computed(() => ctx.activeKey.value === props.fieldKey);

const sessionStore = useSessionStore();
const dateFormat = computed(() => sessionStore.user?.user?.dateFormat || '');

function toDateDisplay(v: string): string {
  if (!v) return '';
  return formatDateOnly(v) || v;
}
function toDateStorage(v: string): string {
  if (!v) return '';
  const iso = normalizeDateInputToIso(v, dateFormat.value);
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : v;
}
function displayOf(v: string): string {
  return isDate.value ? toDateDisplay(v) : v;
}

// 아이콘 클릭 → 주소는 모달, 그 외는 챗봇/입력 라우팅
function onIcon() {
  if (isAddress.value) ctx.openAddress(props.fieldKey);
  else ctx.activate(props.fieldKey);
}
const iconName = computed(() =>
  isDate.value
    ? 'mdi-calendar-month'
    : isAddress.value
      ? 'mdi-map-marker-outline'
      : isMsic.value
        ? 'mdi-briefcase-search-outline'
        : 'mdi-creation',
);
const iconClass = computed(() =>
  isDate.value ? 'ic-date' : isAddress.value ? 'ic-addr' : isMsic.value ? 'ic-msic' : 'ic-ai',
);
const iconTip = computed(() =>
  isDate.value
    ? t('cdd.pickDate')
    : isAddress.value
      ? t('cdd.addrEnter')
      : isMsic.value
        ? 'Look up MSIC / Business Nature'
        : t('cdd.assistant'),
);

// ── combo (nationality / occupation / country / shareClass) ──
// shareClass = Ordinary/Preference/Others 고정 3종. 그 외 정적 JSON.
const comboItems = computed(() => ctx.comboOptionsOf(props.fieldKey));
function onCombo(v: any) { ctx.setFieldValue(props.fieldKey, v ? String(v) : ''); }

// ── 인라인 편집 (combo 아닌 경우) ──
const ed = ref<HTMLElement>();
onMounted(() => { if (ed.value) ed.value.textContent = displayOf(value.value); });
watch(value, async (v) => {
  await nextTick();
  const disp = displayOf(v);
  if (ed.value && document.activeElement !== ed.value && ed.value.textContent !== disp) {
    ed.value.textContent = disp;
  }
});
// 헤더 dateFormat 변경 시 날짜 필드 즉시 재표시
watch(dateFormat, async () => {
  if (!isDate.value) return;
  await nextTick();
  if (ed.value && document.activeElement !== ed.value) {
    ed.value.textContent = toDateDisplay(value.value);
  }
});
function onInput() {
  if (!ed.value) return;
  const txt = ed.value.textContent || '';
  // 다 지웠을 때 브라우저가 남기는 <br> 제거 → :empty 성립 → placeholder 복구
  if (!txt && ed.value.innerHTML) ed.value.innerHTML = '';
  if (isDate.value) {
    if (!txt) {
      ctx.setFieldValue(props.fieldKey, '');
      return;
    }
    const iso = toDateStorage(txt);
    ctx.setFieldValue(props.fieldKey, /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : txt);
    return;
  }
  ctx.setFieldValue(props.fieldKey, txt);
}
function onBlur() {
  if (!isDate.value || !ed.value) return;
  const txt = (ed.value.textContent || '').trim();
  if (!txt) {
    ctx.setFieldValue(props.fieldKey, '');
    return;
  }
  const iso = toDateStorage(txt);
  ctx.setFieldValue(props.fieldKey, iso);
  ed.value.textContent = toDateDisplay(iso);
}
// hover X → 값 비우기 (contenteditable 도 초기화해 placeholder 복구)
function clearField() {
  ctx.setFieldValue(props.fieldKey, '');
  if (ed.value) ed.value.innerHTML = '';
}

// ── 드래그앤드롭 ──
const dragOver = ref(false);
function onDrop(e: DragEvent) {
  dragOver.value = false;
  let v = '';
  const json = e.dataTransfer?.getData('application/x-entity-field');
  if (json) { try { v = String(JSON.parse(json).value ?? ''); } catch { /* noop */ } }
  if (!v) v = e.dataTransfer?.getData('text/plain') || '';
  if (v) {
    const next = isDate.value ? toDateStorage(v.trim()) : v.trim();
    ctx.setFieldValue(props.fieldKey, next);
  }
}

// ── pick-place: 챗봇 copy 로 보관된 값이 있으면, 이 필드 클릭 시 그 값을 삽입 ──
const placeable = computed(() => !!ctx.pendingPickValue.value);
function onFieldMousedown(e: MouseEvent) {
  if (!ctx.pendingPickValue.value) return;
  e.preventDefault(); // contenteditable 포커스/편집 대신 값 삽입
  ctx.placePick(props.fieldKey);
}
</script>

<template>
  <!-- Types of shares — 고정 3종 select (autocomplete 검색 UI 제거) -->
  <v-select
    v-if="isCombo && isShareType"
    class="cddf-combo cddf-combo--share"
    :class="{ filled: !!value }"
    :model-value="value || null"
    :items="comboItems"
    item-title="label"
    item-value="value"
    placeholder="Select type"
    variant="outlined"
    density="compact"
    hide-details
    clearable
    :menu-props="{ maxHeight: 240, contentClass: 'cdd-share-menu' }"
    @update:model-value="onCombo"
    @dragover.prevent="dragOver = true"
    @dragleave="dragOver = false"
    @drop.prevent="onDrop"
  />

  <!-- 그 외 콤보 (nationality / occupation / country) -->
  <v-autocomplete
    v-else-if="isCombo"
    class="cddf-combo" :class="{ filled: !!value }"
    :model-value="value || null"
    :items="comboItems" item-title="label" item-value="value"
    :placeholder="label" variant="outlined" density="compact" hide-details clearable
    :menu-props="{ maxHeight: 320 }"
    @update:model-value="onCombo"
    @dragover.prevent="dragOver = true" @dragleave="dragOver = false" @drop.prevent="onDrop"
  />

  <!-- 그 외: 인라인 편집 + 아이콘 -->
  <span
    v-else
    class="cddf" :class="{ empty: !value, filled: !!value, active, dragover: dragOver, placeable }"
    @mousedown.capture="onFieldMousedown"
    @dragover.prevent="dragOver = true" @dragleave="dragOver = false" @drop.prevent="onDrop"
  >
    <span
      ref="ed" class="cddf-in" :data-ph="label" contenteditable="true" spellcheck="false"
      @input="onInput" @blur="onBlur" @keydown.enter.prevent="ed?.blur()"
    />
    <button
      v-if="showIcon"
      class="cddf-ic" :class="iconClass"
      :title="iconTip"
      @mousedown.prevent @click.stop="onIcon"
    >
      <v-icon size="12">{{ iconName }}</v-icon>
    </button>
    <!-- 값 있을 때 hover 하면 우측에 삭제(X) -->
    <button
      v-if="!!value"
      class="cddf-clear" title="Clear"
      @mousedown.prevent @click.stop="clearField"
    >
      <v-icon size="11">mdi-close</v-icon>
    </button>
  </span>
</template>

<style scoped>
.cddf { display:inline-flex; align-items:center; gap:3px; border-radius:4px; padding:0 3px; vertical-align:baseline; }
.cddf.empty { background:#fff6d6; box-shadow:inset 0 -2px 0 #f2c94c; }
.cddf.filled { background:#eaf3ff; box-shadow:inset 0 -2px 0 #1B84FF; }
.cddf.active { outline:2px solid #1B84FF; outline-offset:1px; }
.cddf.dragover { outline:2px dashed #22a06b; background:#e6f7ee; }
.cddf.placeable { outline:1.5px dashed #7bb0ff; cursor:copy; }
.cddf.placeable:hover { background:#eaf3ff; }
.cddf-in { min-width:46px; outline:none; font:inherit; color:#10305c; white-space:pre-wrap; }
.cddf.filled .cddf-in { font-weight:600; }
.cddf-in:empty::before { content:attr(data-ph); color:#9a7b1a; font-style:italic; font-size:.9em; cursor:text; }
.cddf-ic { display:inline-flex; align-items:center; justify-content:center; width:18px; height:18px; border-radius:50%; border:none; cursor:pointer; flex:0 0 auto; padding:0; }
.cddf-ic.ic-ai { background:#eaf3ff; color:#1B84FF; }
.cddf-ic.ic-date { background:#eafaf1; color:#22a06b; }
.cddf-ic.ic-addr { background:#fdeefe; color:#a23bb0; }
.cddf-ic.ic-msic { background:#fff4e5; color:#d97706; }
.cddf-ic:hover { filter:brightness(.94); }
/* hover 삭제 버튼 — 기본 숨김, 필드에 마우스 올리면 노출 */
.cddf-clear { display:none; align-items:center; justify-content:center; width:15px; height:15px; border-radius:50%; border:none; background:#c4ccd6; color:#fff; cursor:pointer; flex:0 0 auto; padding:0; }
.cddf:hover .cddf-clear { display:inline-flex; }
.cddf-clear:hover { background:#8b95a1; }

/* 인라인 콤보박스 — 필드 전체가 클릭 대상. 폰트/높이를 문서 본문(12px)에 맞춰 컴팩트하게. */
.cddf-combo { display:inline-flex; min-width:190px; max-width:360px; vertical-align:middle; font-size:12px; line-height:1.3; }
.cddf-combo :deep(.v-field) { cursor:pointer; border-radius:6px; background:#fff6d6; min-height:26px; font-size:12px; }
.cddf-combo.filled :deep(.v-field) { background:#eaf3ff; }
.cddf-combo :deep(.v-field__field) { min-height:26px; }
.cddf-combo :deep(.v-field__input) { min-height:26px; padding:0 6px; font-size:12px; line-height:1.3; color:#10305c; align-items:center; flex-wrap:nowrap; overflow:visible; }
/* 선택값 "..." 잘림 방지 — ellipsis 대신 전체 표시 */
.cddf-combo :deep(.v-select__selection) { max-width:none; overflow:visible; }
.cddf-combo :deep(.v-select__selection-text),
.cddf-combo :deep(.v-autocomplete__selection-text) { overflow:visible; text-overflow:clip; white-space:nowrap; }
/* 선택된 값 텍스트·입력 요소 — Vuetify 기본(16px) 무시하고 12px 강제 */
.cddf-combo :deep(.v-field__input),
.cddf-combo :deep(.v-select__selection),
.cddf-combo :deep(.v-select__selection-text),
.cddf-combo :deep(.v-autocomplete__selection-text),
.cddf-combo :deep(input) { font-size:12px !important; line-height:1.3; letter-spacing:normal; }
.cddf-combo.filled :deep(.v-select__selection-text),
.cddf-combo.filled :deep(.v-field__input) { font-weight:600; }
.cddf-combo :deep(input::placeholder) { color:#9a7b1a; font-style:italic; opacity:1; font-size:12px; }
.cddf-combo :deep(.v-field__append-inner) { padding-top:2px; }
.cddf-combo :deep(.v-field__append-inner .v-icon) { font-size:16px; }
.cddf-combo :deep(.v-field__outline) { --v-field-border-opacity:0.25; }

/* Types of shares — 빈칸: 넓게 + 노란 placeholder / 선택: 좌·우 필드와 동일 파란 filled */
.cddf-combo--share {
  display: block;
  width: 100%;
  min-width: 168px;
  max-width: none;
}
.cddf-combo--share :deep(.v-input__control),
.cddf-combo--share :deep(.v-field) {
  width: 100%;
}
.cddf-combo--share :deep(.v-field) {
  border-radius: 4px;
  background: #fff6d6;
  min-height: 26px;
  box-shadow: inset 0 -2px 0 #f2c94c;
}
.cddf-combo--share.filled :deep(.v-field) {
  background: #eaf3ff;
  box-shadow: inset 0 -2px 0 #1B84FF;
}
.cddf-combo--share :deep(.v-field__outline) {
  display: none;
}
.cddf-combo--share :deep(.v-field--focused) {
  box-shadow: inset 0 -2px 0 #1B84FF;
  outline: 2px solid #1B84FF;
  outline-offset: 1px;
}
.cddf-combo--share :deep(.v-field__field),
.cddf-combo--share :deep(.v-field__input) {
  min-height: 26px;
}
.cddf-combo--share :deep(.v-field__input) {
  padding: 1px 6px;
  color: #10305c;
}
.cddf-combo--share :deep(input::placeholder) {
  color: #9a7b1a;
  font-style: italic;
  opacity: 1;
  font-size: 12px;
}
.cddf-combo--share.filled :deep(.v-select__selection-text),
.cddf-combo--share.filled :deep(.v-field__input) {
  font-weight: 600;
  color: #10305c;
}
.cddf-combo--share :deep(.v-field__clearable) {
  margin-inline-start: 0;
  opacity: 0.55;
}
.cddf-combo--share :deep(.v-field__append-inner) { padding-top: 2px; }
.cddf-combo--share :deep(.v-field__append-inner .v-icon) { font-size: 16px; opacity: 0.75; }
</style>

<!-- 메뉴는 teleport → scoped 밖에 스타일 -->
<style>
.cdd-share-menu.v-overlay__content,
.cdd-share-menu {
  border-radius: 10px !important;
  box-shadow: 0 8px 24px rgba(20, 30, 60, 0.14) !important;
  padding: 4px !important;
}
.cdd-share-menu .v-list {
  padding: 2px !important;
  background: #fff !important;
}
.cdd-share-menu .v-list-item {
  min-height: 36px !important;
  border-radius: 8px !important;
  margin: 1px 0 !important;
  font-size: 13px !important;
  color: #222 !important;
}
.cdd-share-menu .v-list-item--active,
.cdd-share-menu .v-list-item--link:hover {
  background: #eaf3ff !important;
  color: #1B84FF !important;
}
.cdd-share-menu .v-list-item-title {
  font-size: 13px !important;
  line-height: 1.3 !important;
}
</style>
